import React, { FC } from "react";
import { orderBy } from "lodash";
import CollapsibleSection from "../../components/ui/CollapsibleSection";
import { DecisionIntelligenceItem } from "../../components/DecisionIntelligenceItem";
import { DecisionStatusLog } from "../../models/applications/applicationInterfaces";
import { CheckCircleFilled, WarningFilled } from "@ant-design/icons";

export interface ExecutedRulesV2Props {
  decisionStatusLog?: DecisionStatusLog;
  className?: string;
}

const LIGHTGRAY = "#f6f6f6";
const ORANGE = "#FA8C16";
const LIGHTRED = "#fde8e9";
const LIGHTORANGE = "#fff7e6";
const RED = "#eb2835";
const TEAL = "#00b4a1";
const ExecutedRulesV2: FC<ExecutedRulesV2Props> = ({
  decisionStatusLog,
  className,
}) => {
  if (!decisionStatusLog) {
    return (
      <div
        className="mb-3 align-items-center d-flex justify-content-center flex-column"
        style={{
          backgroundColor: "#f0f0f0",
          borderRadius: "10px",
          padding: "28px",
        }}
      >
        <WarningFilled
          className="mb-2"
          style={{ fontSize: "28px", color: TEAL }}
        />
        <h4 className="fwt f-5">
          Rules have not been run for this application
        </h4>
      </div>
    );
  }

  const executedRules = orderBy(
    decisionStatusLog.availableDecisionRules,
    (rule) => rule.executionOrder
  );

  const matchedDeclinedRules = executedRules.filter(
    (rule) => rule.matched && rule.decisionStatus?.decision === "denied"
  );

  const matchedReviewRules = executedRules.filter(
    (rule) => rule.matched && rule.decisionStatus?.decision === "review"
  );

  // RENDER -----------------------------------------------------------------------
  return (
    <div className={className}>
      {/* ALL CHECKS COMPLETE */}
      {/* --- Only displays if there are no declined or review rules */}
      {matchedDeclinedRules.length === 0 && matchedReviewRules.length === 0 && (
        <div
          className="mb-3 align-items-center d-flex justify-content-center flex-column"
          style={{
            backgroundColor: LIGHTGRAY,
            borderRadius: "10px",
            padding: "28px",
          }}
        >
          <CheckCircleFilled
            className="mb-2"
            style={{ fontSize: "28px", color: TEAL }}
          />
          <h4 className="fwt f-5 mb-2">All Checks Complete</h4>
        </div>
      )}

      {/*** DECLINED CHECKS TABLE ***/}
      {matchedDeclinedRules.length > 0 && (
        <div
          className="mb-3"
          style={{
            backgroundColor: LIGHTRED,
            borderRadius: "10px",
            padding: "28px",
          }}
        >
          <h4 className="fwt f-5 mb-2" style={{ color: RED }}>
            Declined
          </h4>

          {matchedDeclinedRules.map((rule) => (
            <DecisionIntelligenceItem key={rule.uuid} rule={rule} />
          ))}
        </div>
      )}
      {/* REVIEW CHECKS TABLE */}
      {matchedReviewRules.length > 0 && (
        <div
          className="mb-3"
          style={{
            backgroundColor: LIGHTORANGE,
            borderRadius: "10px",
            padding: "28px",
          }}
        >
          <h4 style={{ color: ORANGE }} className="fwt f-5 mb-2">
            Review
          </h4>

          {matchedReviewRules.map((rule) => (
            <DecisionIntelligenceItem key={rule.uuid} rule={rule} />
          ))}
        </div>
      )}
      {/*** ALL CHECKS TABLE (Can be Expanded/Collapsed) ***/}
      <CollapsibleSection title="All Checks">
        <div className="d-flex flex-column">
          {executedRules.map((rule) => (
            <DecisionIntelligenceItem
              key={rule.uuid}
              rule={rule}
              allowApprovalColor={
                // Only allow approval status to be colored if there are no declined and review rules
                matchedDeclinedRules.length === 0 &&
                matchedReviewRules.length === 0
              }
            />
          ))}
        </div>
      </CollapsibleSection>
    </div>
  );
};

export default ExecutedRulesV2;
